/**
 * Composite Citation Split View
 *
 * Side-by-side comparison view for citations that combine data from multiple files.
 * Used for discrepancy detection, cross-file analysis, and data aggregation.
 */

import React, { useState } from 'react';

import {
  Link2,
  FileText,
  FileSpreadsheet,
  Presentation,
  Table,
  ArrowLeftRight,
  Plus,
  Minus,
  Equal,
  AlertTriangle,
  CheckCircle2,
  X,
  Maximize2,
  Minimize2,
  ExternalLink,
} from 'lucide-react';

// File type icons
const FILE_ICONS = {
  pdf: FileText,
  excel: FileSpreadsheet,
  csv: Table,
  word: FileText,
  powerpoint: Presentation,
  document: FileText,
};

// Relationship type configuration
const RELATIONSHIP_CONFIG = {
  comparison: {
    label: 'Comparison',
    icon: ArrowLeftRight,
    color: 'blue',
    description: 'Values compared across files',
  },
  aggregation: {
    label: 'Combined Total',
    icon: Plus,
    color: 'emerald',
    description: 'Values aggregated from multiple sources',
  },
  discrepancy: {
    label: 'Discrepancy Detected',
    icon: AlertTriangle,
    color: 'amber',
    description: 'Conflicting values found',
  },
  correlation: {
    label: 'Correlated Data',
    icon: Link2,
    color: 'violet',
    description: 'Related data points across files',
  },
  merge: {
    label: 'Merged',
    icon: CheckCircle2,
    color: 'slate',
    description: 'Data merged from sources',
  },
};

/**
 * Source Panel - Displays one source in the split view
 */
function SourcePanel({ source, index, onViewSource, isHighlighted }) {
  const FileIcon = FILE_ICONS[source.fileType || 'document'] || FileText;

  return (
    <div
      className={`
      flex-1 min-w-[200px] p-4 rounded-xl border transition-all
      ${
        isHighlighted
          ? 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10'
          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'
      }
    `}
    >
      {/* Source Header */}
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100 dark:border-slate-800">
        <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
          <FileIcon className="h-4 w-4 text-slate-600 dark:text-slate-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 dark:text-white truncate">
            {source.fileName || `Source ${index + 1}`}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {source.location?.page && `Page ${source.location.page}`}
            {source.location?.sheet && `Sheet "${source.location.sheet}"`}
            {source.location?.cell && ` • ${source.location.cell}`}
          </div>
        </div>
        <button
          onClick={() => onViewSource?.(source)}
          className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
          title="View source"
        >
          <ExternalLink className="h-4 w-4" />
        </button>
      </div>

      {/* Source Value */}
      {source.value !== undefined && (
        <div
          className={`
          p-3 rounded-lg text-center mb-3
          ${
            isHighlighted
              ? 'bg-amber-100/50 dark:bg-amber-900/20'
              : 'bg-slate-50 dark:bg-slate-800/50'
          }
        `}
        >
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            {typeof source.value === 'number' ? source.value.toLocaleString() : source.value}
          </div>
          {source.valueLabel && (
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {source.valueLabel}
            </div>
          )}
        </div>
      )}

      {/* Source Text */}
      {source.extractedText && (
        <blockquote className="text-sm text-slate-600 dark:text-slate-300 italic border-l-2 border-slate-200 dark:border-slate-700 pl-3 line-clamp-4">
          "{source.extractedText}"
        </blockquote>
      )}

      {/* Source Metadata */}
      {source.metadata && (
        <div className="mt-3 pt-2 border-t border-slate-100 dark:border-slate-800">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
            Details
          </div>
          <div className="space-y-0.5">
            {Object.entries(source.metadata).map(([key, value]) => (
              <div key={key} className="flex justify-between text-xs">
                <span className="text-slate-500 dark:text-slate-400">{key}:</span>
                <span className="text-slate-700 dark:text-slate-300 font-medium">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Relationship Indicator - Shows how sources are related
 */
function RelationshipIndicator({ relationship, diffValue }) {
  const config = RELATIONSHIP_CONFIG[relationship] || RELATIONSHIP_CONFIG.comparison;
  const IconComponent = config.icon;

  return (
    <div className="flex flex-col items-center justify-center px-4">
      <div
        className={`
        w-12 h-12 rounded-full flex items-center justify-center
        bg-${config.color}-100 dark:bg-${config.color}-900/30
        text-${config.color}-600 dark:text-${config.color}-400
      `}
      >
        <IconComponent className="h-6 w-6" />
      </div>

      <div
        className={`
        mt-2 px-2 py-1 rounded-full text-xs font-medium
        bg-${config.color}-100 dark:bg-${config.color}-900/30
        text-${config.color}-700 dark:text-${config.color}-300
      `}
      >
        {config.label}
      </div>

      {diffValue && (
        <div
          className={`
          mt-1 text-sm font-mono font-bold
          ${diffValue.startsWith('-') ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}
        `}
        >
          {diffValue}
        </div>
      )}

      <div className="mt-1 text-[10px] text-slate-500 dark:text-slate-400 text-center max-w-[100px]">
        {config.description}
      </div>
    </div>
  );
}

/**
 * Composite Citation View Component
 */
export function CompositeView({
  citation,
  onViewSource,
  onClose,
  isFullscreen = false,
  onToggleFullscreen,
}) {
  const [highlightedSource, setHighlightedSource] = useState(null);

  const sources = citation?.composite?.sources || [];
  const relationship = citation?.composite?.relationship || 'comparison';
  const diffValue = citation?.composite?.diffValue;
  const synthesisLogic = citation?.composite?.synthesisLogic;

  if (sources.length === 0) {
    return (
      <div className="p-8 text-center text-slate-500 dark:text-slate-400">
        No composite sources available
      </div>
    );
  }

  return (
    <div
      className={`
      ${
        isFullscreen
          ? 'fixed inset-4 z-50 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl'
          : 'relative'
      }
      flex flex-col
    `}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Link2 className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Cross-File Citation
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {sources.length} sources combined •{' '}
              {RELATIONSHIP_CONFIG[relationship]?.label || 'Comparison'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onToggleFullscreen}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
          </button>

          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Split View Content */}
      <div className="flex-1 overflow-auto p-4">
        <div className="flex items-stretch gap-4 min-h-[300px]">
          {sources.map((source, index) => (
            <React.Fragment key={source.fileId || index}>
              <SourcePanel
                source={source}
                index={index}
                onViewSource={onViewSource}
                isHighlighted={highlightedSource === index}
              />

              {/* Show relationship indicator between sources */}
              {index < sources.length - 1 && (
                <RelationshipIndicator
                  relationship={relationship}
                  diffValue={index === 0 ? diffValue : null}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Synthesis Logic */}
        {synthesisLogic && (
          <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
              AI Analysis
            </div>
            <p className="text-sm text-slate-700 dark:text-slate-300">{synthesisLogic}</p>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Combined from {sources.length} documents
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onViewSource?.(sources[0])}
            className="px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            View First Source
          </button>
          <button
            onClick={() => onViewSource?.(sources[sources.length - 1])}
            className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
          >
            View Last Source
          </button>
        </div>
      </div>

      {/* Fullscreen backdrop */}
      {isFullscreen && (
        <div className="fixed inset-0 bg-black/50 -z-10" onClick={onToggleFullscreen} />
      )}
    </div>
  );
}

export default CompositeView;
